<template>
  <Container :judul="news.title">
    <template #header>
      <HeaderFe />
    </template>
    <template v-slot:default>
      <div class="min-h-screen pb-20 bg-gray-50/40">
        <div
          class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 pt-28"
        >
          <!-- Back link -->
          <Link
            :href="route('informasi')"
            class="inline-flex items-center gap-2 mb-6 text-sm font-medium text-gray-500 transition-colors hover:text-primary"
          >
            <i class="fa-solid fa-arrow-left"></i>
            Kembali ke Informasi
          </Link>

          <div class="flex flex-col gap-8 lg:flex-row">
            <!-- Article -->
            <article class="flex-1 min-w-0">
              <div
                class="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl"
              >
                <div
                  v-if="heroImage"
                  class="aspect-[16/9] bg-gray-100 overflow-hidden"
                >
                  <img
                    :src="heroImage"
                    :alt="news.data.title"
                    class="object-cover w-full h-full"
                  />
                </div>

                <div class="p-6 sm:p-8 md:p-10">
                  <div
                    v-if="news.data.tags && news.data.tags.length"
                    class="flex flex-wrap gap-2 mb-4"
                  >
                    <span
                      v-for="tag in news.data.tags"
                      :key="tag"
                      class="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full text-primary bg-primary/10"
                    >
                      #{{ tag }}
                    </span>
                  </div>

                  <h1
                    class="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl lg:text-4xl font-roboto-slab"
                  >
                    {{ news.data.title }}
                  </h1>

                  <div
                    class="flex flex-wrap items-center gap-4 pb-6 mt-4 text-sm text-gray-500 border-b border-gray-100"
                  >
                    <span
                      v-if="news.data.author"
                      class="inline-flex items-center gap-1.5"
                    >
                      <i class="fa-regular fa-user"></i>
                      {{ news.data.author }}
                    </span>
                    <span class="inline-flex items-center gap-1.5">
                      <i class="fa-regular fa-calendar"></i>
                      {{ news.data.publish_date }}
                    </span>
                  </div>

                  <div
                    v-html="news.data.content"
                    class="mt-6 leading-relaxed text-gray-700 news-detail-content"
                  ></div>
                </div>
              </div>
            </article>

            <!-- Sidebar -->
            <aside class="w-full lg:w-80 shrink-0">
              <div class="space-y-6 lg:sticky lg:top-24">
                <!-- Search -->
                <div
                  class="p-2 bg-white border border-gray-100 shadow-sm rounded-2xl"
                >
                  <div class="relative">
                    <div
                      class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none"
                    >
                      <i class="fa-solid fa-magnifying-glass"></i>
                    </div>
                    <input
                      v-model="search"
                      type="text"
                      placeholder="Cari berita lainnya..."
                      class="block w-full py-2.5 pl-10 pr-3 text-sm text-gray-900 bg-white border border-gray-200 rounded-xl placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition"
                    />
                  </div>
                </div>

                <!-- Other news -->
                <div
                  class="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl"
                >
                  <div class="px-5 py-4 border-b border-gray-100">
                    <h3 class="font-bold text-gray-900 font-roboto-slab">
                      Berita Lainnya
                    </h3>
                  </div>
                  <ul class="divide-y divide-gray-100">
                    <li v-for="item in beritaLainnya" :key="item.id">
                      <Link
                        :href="`/read/${item.slug}`"
                        class="flex gap-3 p-4 transition-colors group hover:bg-gray-50"
                      >
                        <img
                          v-if="firstImage(item.image)"
                          :src="firstImage(item.image)"
                          :alt="item.title"
                          class="object-cover w-16 h-16 rounded-lg shrink-0 ring-1 ring-gray-100"
                        />
                        <div
                          v-else
                          class="flex items-center justify-center w-16 h-16 text-gray-300 bg-gray-100 rounded-lg shrink-0"
                        >
                          <i class="fa-regular fa-image"></i>
                        </div>
                        <div class="min-w-0">
                          <p
                            class="text-sm font-semibold text-gray-900 transition-colors line-clamp-2 group-hover:text-primary"
                          >
                            {{ item.title }}
                          </p>
                          <span
                            class="inline-flex items-center gap-1 mt-1 text-xs text-gray-400"
                          >
                            <i class="fa-regular fa-calendar"></i>
                            {{ item.publish_date }}
                          </span>
                        </div>
                      </Link>
                    </li>
                    <li
                      v-if="!beritaLainnya.length"
                      class="p-4 text-sm text-center text-gray-400"
                    >
                      Tidak ada berita lain.
                    </li>
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { defineProps, ref, computed } from "vue";
import { Link } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";

const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
  otherNews: {
    type: [Array, Object],
    default: () => [],
  },
});

const search = ref("");

// `image` is cast to an array on the model; return the first usable URL.
const firstImage = (image) => {
  if (Array.isArray(image)) return image[0] || null;
  if (typeof image === "string" && image) return image;
  return null;
};

const heroImage = computed(() => firstImage(props.news?.data?.image));

// Hanya gunakan props.otherNews dari backend
const beritaLainnya = computed(() => {
  // Jika otherNews adalah objek dengan properti data (Inertia resource collection)
  const arr = Array.isArray(props.otherNews)
    ? props.otherNews
    : props.otherNews && Array.isArray(props.otherNews.data)
    ? props.otherNews.data
    : [];
  const list = arr.filter((item) => item.id !== props.news.data.id);
  if (!search.value) return list;
  return list.filter((item) =>
    item.title.toLowerCase().includes(search.value.toLowerCase())
  );
});
</script>

<style scoped>
/* Support for Quill editor classes from backend */
.news-detail-content :deep(.ql-align-justify) {
  text-align: justify;
}

.news-detail-content :deep(.ql-align-center) {
  text-align: center;
}

.news-detail-content :deep(.ql-align-right) {
  text-align: right;
}

.news-detail-content :deep(.ql-align-left) {
  text-align: left;
}

/* Custom styles for HTML content - scoped only to news-detail-content */
.news-detail-content :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.news-detail-content :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.news-detail-content :deep(ol li),
.news-detail-content :deep(ul li) {
  margin-bottom: 1rem;
  line-height: 1.6;
  padding-left: 0.5rem;
}

.news-detail-content :deep(ol li strong),
.news-detail-content :deep(ul li strong) {
  font-weight: 600;
  color: #1f2937;
}

/* Typography for paragraphs */
.news-detail-content :deep(p) {
  margin: 0.75rem 0;
  line-height: 1.7;
  color: #374151;
}

/* Additional typography support */
.news-detail-content :deep(h1),
.news-detail-content :deep(h2),
.news-detail-content :deep(h3),
.news-detail-content :deep(h4),
.news-detail-content :deep(h5),
.news-detail-content :deep(h6) {
  font-weight: 600;
  margin: 1.5rem 0 0.75rem 0;
  color: #1f2937;
}

.news-detail-content :deep(br) {
  line-height: 1;
}

/* Support for other Quill formatting */
.news-detail-content :deep(strong) {
  font-weight: 600;
}

.news-detail-content :deep(em) {
  font-style: italic;
}

.news-detail-content :deep(u) {
  text-decoration: underline;
}

/* Additional styling for better readability */
.news-detail-content :deep(blockquote) {
  border-left: 4px solid #e5e7eb;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #6b7280;
}

.news-detail-content :deep(code) {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas,
    "Liberation Mono", Menlo, monospace;
  font-size: 0.875em;
}

.news-detail-content :deep(pre) {
  background-color: #f3f4f6;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1rem 0;
}

.news-detail-content :deep(img) {
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 200px;
  max-height: 500px;
  object-fit: cover;
  object-position: center;
  border-radius: 0.5rem;
  margin: 1rem 0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

/* Responsive image sizing */
@media (max-width: 640px) {
  .news-detail-content :deep(img) {
    min-height: 150px;
    max-height: 300px;
    margin: 0.75rem 0;
    border-radius: 0.375rem;
  }
}

@media (min-width: 641px) and (max-width: 768px) {
  .news-detail-content :deep(img) {
    min-height: 180px;
    max-height: 400px;
  }
}

@media (min-width: 769px) {
  .news-detail-content :deep(img) {
    min-height: 200px;
    max-height: 500px;
  }
}

/* Hover effect for images */
.news-detail-content :deep(img:hover) {
  transform: scale(1.02);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

/* Image container for better layout */
.news-detail-content :deep(p img) {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

/* Multiple images in same paragraph */
.news-detail-content :deep(p) {
  text-align: justify;
}

.news-detail-content :deep(p:has(img)) {
  text-align: center;
}

/* Image figure and caption support */
.news-detail-content :deep(figure) {
  margin: 1.5rem 0;
  text-align: center;
}

.news-detail-content :deep(figure img) {
  margin-bottom: 0.5rem;
}

.news-detail-content :deep(figcaption) {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
  text-align: center;
  margin-top: 0.5rem;
  padding: 0 1rem;
}

/* Loading placeholder for images */
.news-detail-content :deep(img[src=""]),
.news-detail-content :deep(img:not([src])) {
  background-color: #f3f4f6;
  background-image: linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
    linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
    linear-gradient(-45deg, transparent 75%, #e5e7eb 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  animation: loading 1s linear infinite;
}

@keyframes loading {
  0% {
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  }
  100% {
    background-position: 20px 20px, 20px 30px, 30px 10px, 10px 20px;
  }
}

/* Image gallery styling for multiple images */
.news-detail-content :deep(.image-gallery) {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1.5rem 0;
}

.news-detail-content :deep(.image-gallery img) {
  margin: 0;
  width: 100%;
  height: 200px;
  object-fit: cover;
}

/* Utility classes for different image alignments from editor */
.news-detail-content :deep(.ql-align-center img),
.news-detail-content :deep(.text-center img),
.news-detail-content :deep(img.center) {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.news-detail-content :deep(.ql-align-left img),
.news-detail-content :deep(.text-left img),
.news-detail-content :deep(img.left) {
  float: left;
  margin: 0 1rem 1rem 0;
  max-width: 50%;
}

.news-detail-content :deep(.ql-align-right img),
.news-detail-content :deep(.text-right img),
.news-detail-content :deep(img.right) {
  float: right;
  margin: 0 0 1rem 1rem;
  max-width: 50%;
}

/* Clear floats after floated images */
.news-detail-content :deep(.ql-align-left):after,
.news-detail-content :deep(.ql-align-right):after,
.news-detail-content :deep(.text-left):after,
.news-detail-content :deep(.text-right):after {
  content: "";
  display: table;
  clear: both;
}

/* Full width images */
.news-detail-content :deep(img.full-width) {
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
}

/* Small inline images */
.news-detail-content :deep(img.inline-small) {
  display: inline-block;
  max-width: 150px;
  max-height: 100px;
  margin: 0 0.5rem;
  vertical-align: middle;
}

/* Mobile specific adjustments */
@media (max-width: 640px) {
  .news-detail-content :deep(.image-gallery) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .news-detail-content :deep(.image-gallery img) {
    height: 180px;
  }

  .news-detail-content :deep(figcaption) {
    font-size: 0.8rem;
    padding: 0 0.5rem;
  }

  .news-detail-content :deep(.ql-align-left img),
  .news-detail-content :deep(.text-left img),
  .news-detail-content :deep(img.left),
  .news-detail-content :deep(.ql-align-right img),
  .news-detail-content :deep(.text-right img),
  .news-detail-content :deep(img.right) {
    float: none;
    display: block;
    margin: 1rem auto;
    max-width: 100%;
  }

  .news-detail-content :deep(img.full-width) {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  .news-detail-content :deep(img.inline-small) {
    display: block;
    margin: 0.5rem auto;
  }
}
</style>
